<template>
  <BaseOverlay :show="fetching">
    <div>
      <popup-notification
        :title="notification.title"
        :description="notification.description"
        :publish-handler="publishNotification"
        :test-notification-handler="testNotificationHandler"
      />
      <validation-observer ref="createNotificationRef">
        <b-form>
          <b-form-group>
            <validation-provider
              #default="{ errors }"
              name="title"
              rules="required|max:65"
            >
              <b-form-input
                id="title"
                v-model="notification.title"
                :state="errors.length > 0 ? false : null"
                name="title"
                placeholder="Enter Title"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group>
            <validation-provider
              #default="{ errors }"
              name="description"
              :rules="descriptionRules"
            >
              <b-form-textarea
                id="description"
                v-model="notification.description"
                :state="errors.length > 0 ? false : null"
                name="description"
                placeholder="Enter description"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group>
            <b-form-file
              v-model="notificationImage"
              placeholder="Attach Image"
              @input="selectNotificationImage"
            />
            <b-img
              v-if="imagePreviewURL"
              style="cursor:pointer"
              class="mt-2"
              :src="imagePreviewURL"
              fluid
              rounded
              width="200"
              height="150"
              @click="previewImageInNewTab"
            />
          </b-form-group>
          <b-form-group>
            <validation-provider
              #default="{ errors }"
              name="title"
              rules="url"
            >
              <b-form-input
                v-model="notification.url"
                placeholder="Enter Link"
              />
              <small
                class="text-danger"
              >{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-checkbox
            v-model="displayInApp"
            switch
            class="font-weight-bolder"
          >Display the notification in the rider/driver app</b-form-checkbox>
          <div class="w-50 d-flex">
            <validation-provider
              v-if="displayInApp"
              #default="{ errors }"
              name="Expiry Date"
              rules="required"
            >
              <div class="expiry-date">
                <label>Expiry Date</label>
                <b-form-datepicker
                  v-model="currentExpiryDate"
                  value-as-date
                  :min="new Date()"
                />
              </div>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
            <validation-provider
              v-if="displayInApp"
              #default="{ errors }"
              name="Expiry Time"
              rules="required"
            >
              <div>
                <label>Expiry Time</label>
                <b-form-timepicker v-model="currentExpiryTime" />
              </div>
              <small class="text-danger">{{ errors[0] }}</small>
              <div
                v-if="!isTimePresent"
                class="text-danger"
              >
                <small>The time you selected is in the past.</small>
              </div>
            </validation-provider>
          </div>

          <template v-if="displayInApp">
            <b-form-group
              label="Reference Type"
              class="mt-1"
            >
              <BFormSelect
                v-model="notification.reference_type"
                :options="referenceTypes"
              />
            </b-form-group>
            <b-form-group
              v-if="notification.reference_type == 'promo' || notification.reference_type == 'URL'"
              label="Reference Value"
            >
              <validation-provider
                #default="{ errors }"
                name="Reference Value"
                rules="required"
              >
                <b-form-input
                  v-model="notification.reference_value"
                  :state="errors.length > 0 ? false : null"
                  name="Reference Value"
                  placeholder="Enter Reference Value"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <b-form-group
              v-if="notification.reference_type == 'URL'"
              label="Cta Label English"
            >
              <validation-provider
                #default="{ errors }"
                name="Cta Label English"
                rules="required"
              >
                <b-form-input
                  v-model="notification.cta_label_english"
                  name="Cta Label English"
                  placeholder="Enter Cta Label English"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <b-form-group
              v-if="notification.reference_type == 'URL'"
              label="Cta Label Arabic"
            >
              <validation-provider
                #default="{ errors }"
                name="Cta Label Arabic"
                rules="required"
              >
                <b-form-input
                  v-model="notification.cta_label_arabic"
                  name="Cta Label Arabic"
                  placeholder="Enter Cta Label Arabic"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </template>

          <div class="mt-2">
            <h3>Target Users</h3>
            <div class="d-flex flex-column my-1 font-weight-bolder">
              <b-form-checkbox
                v-model="groupsNotification"
                switch
                @change="onGroupsNotification"
              >
                Groups Notification
              </b-form-checkbox>
              <div v-if="groupsNotification">
                <v-select
                  v-model="selectedGroup"
                  placeholder="Select group"
                  label="name"
                  :clearable="false"
                  :options="groups"
                  class="mb-2"
                  @search="fetchGroups"
                />
              </div>
              <b-form-checkbox
                v-model="topicNotification"
                switch
                @change="onTopicNotification"
              >
                Topic Notification
              </b-form-checkbox>
              <div
                v-if="topicNotification"
                class="d-flex mb-2 checkbox-section"
              >
                <b-form-checkbox-group
                  v-model="topic"
                  :options="topicOptions"
                  value-field="key"
                  text-field="label"
                />
              </div>
              <small
                v-if="topicNotification && showErrorMessage"
                class="text-danger"
              >You should choose at least one of these fields.</small>
              <b-form-checkbox
                v-model="individualNotification"
                switch
                @change="onIndividualNotification"
              >
                Individual Notification
              </b-form-checkbox>
              <div
                v-if="individualNotification"
                class="mb-2"
              >
                <v-select
                  v-model="userType"
                  placeholder="Select user type"
                  label="User type"
                  :options="userTypes"
                  class="mb-2"
                  :clearable="false"
                  @input="notification.type = userType"
                />
                <DriversSearch
                  v-if="userType == 'driver'"
                  :auto-reset="false"
                  :clearable="false"
                  :filterable="false"
                  @drivers-selection="
                    notification.userId = $event.externalId;
                    notification.phoneNumber = $event.phoneNumber;
                  "
                />
                <UsersSearch
                  v-if="userType == 'user'"
                  :value="notification.phoneNumber"
                  :auto-reset="false"
                  :clearable="false"
                  :filterable="false"
                  @users-selection="
                    notification.userId = $event.externalId;
                    notification.phoneNumber = $event.phoneNumber;
                  "
                />
                <small
                  v-if="showErrorMessage2"
                  class="text-danger"
                >
                  Please select a {{ userType }}
                </small>
              </div>
            </div>
          </div>
          <div class="d-flex justify-content-between">
            <b-button
              variant="outline-primary"
              @click="$router.push('/notifications')"
            >
              Cancel
            </b-button>
            <div class="d-flex justify-content-between">
              <b-button
                variant="outline-primary mr-1"
                :disabled="loadingDraftBtn"
                @click="saveAsDraft"
              >
                <b-spinner
                  v-if="loadingDraftBtn"
                  small
                />
                <span v-else>Save as Draft</span>
              </b-button>
              <b-button
                variant="primary"
                @click="open"
              >
                Submit
              </b-button>
            </div>
          </div>
        </b-form>
      </validation-observer>
    </div>
  </BaseOverlay>
</template>

<script>
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BFormGroup,
  BFormInput,
  BForm,
  BFormTextarea,
  BButton,
  BFormCheckboxGroup,
  BFormCheckbox,
  BFormDatepicker,
  BFormTimepicker,
  BSpinner,
  BFormFile,
  BFormSelect,
  BImg,
} from 'bootstrap-vue'
import { required, url } from '@validations'
import { max } from '@/utils/validations'
import BaseOverlay from '@/UI/BaseOverlay.vue'
import useRegisterStoreModule from '@/hooks/useRegisterStoreModule'
import notificationsStore from '../store'
import DriversSearch from '@/views/driver/components/DriversSearch.vue'
import UsersSearch from '@/views/users/rider/components/UsersSearch.vue'
import PopupNotification from '../components/PopupNotification.vue'
import useCreateNotification from '../hooks/useCreateNotification'
import usersStore from '@/views/users/rider/store'
import groupsStore from '@/views/marketing/groups/store'

export default {
  components: {
    BFormGroup,
    BFormInput,
    BImg,
    BFormSelect,
    BForm,
    BFormTextarea,
    ValidationProvider,
    ValidationObserver,
    BButton,
    BFormCheckboxGroup,
    BFormCheckbox,
    BFormFile,
    DriversSearch,
    UsersSearch,
    BaseOverlay,
    vSelect,
    BFormDatepicker,
    BFormTimepicker,
    PopupNotification,
    BSpinner,
  },
  setup(_, { root }) {
    useRegisterStoreModule('notifications', notificationsStore)
    useRegisterStoreModule('users', usersStore)
    useRegisterStoreModule('groups', groupsStore)
    return {
      required,
      max,
      url,
      ...useCreateNotification(root),
    }
  },
}
</script>

<style scoped>
.checkbox-section {
  gap: 60px;
}
.expiry-date {
  margin-right: 24px;
}
</style>
